/**
 * Row filters for the `oem-source` drawer — the demo's `audit(key, filter)`
 * filter object, made serialisable.
 *
 * The demo's filter (line 1907) maps a column to one of three things: a value
 * (exact match), an array (any of), or a function `(value, row) => boolean`.
 * Twenty-odd call sites pass a function, almost all of them for one of two
 * shapes: a date window (`d => d >= '2026-08-31' && d <= '2026-09-06'`, or
 * `c => inWk(c.slice(0, 10))` on a timestamp) and "below the row's own
 * minimum" (`(v, r) => v < r.min_level`).
 *
 * A function cannot travel on a drawer payload or through a seed file, so the
 * port expresses those shapes as data instead: `{ between: [lo, hi] }`,
 * `{ below: 'min_level' }` and `{ not: value }`. The matcher is pure and has no
 * React in it; `describeFilter` prints the demo's `k=v, k=a|b` provenance
 * suffix.
 *
 * Shared layer, fork-owned (zone C).
 *
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx — where the rows land
 * @see src/versions/v1/modules/issues/dataset-source.ts — the fetching opener
 */

/** One column's condition. */
export type RowMatcher =
  /** Exact match, compared as strings — the demo's `r[k] === v`. */
  | string
  | number
  | boolean
  /** Any of — the demo's `Array.isArray(v) ? v.includes(r[k])`. */
  | readonly (string | number)[]
  /**
   * Inclusive range. Numbers compare as numbers; strings compare on the
   * value's first `hi.length` characters, so `['2026-08-31', '2026-09-06']`
   * matches a date and a `2026-09-06T14:22` timestamp alike.
   */
  | { readonly between: readonly [string | number, string | number] }
  /** The value is below another column on the same row. */
  | { readonly below: string }
  /** Anything except this value. */
  | { readonly not: string | number };

/** Column → condition. Every condition must hold. */
export type RowFilter = Readonly<Record<string, RowMatcher>>;

/** A dataset row, addressed by column name. */
type Row = Readonly<Record<string, unknown>>;

function inRange(value: unknown, lo: string | number, hi: string | number): boolean {
  if (value == null) return false;
  if (typeof lo === 'number' && typeof hi === 'number') {
    const n = Number(value);
    return !Number.isNaN(n) && n >= lo && n <= hi;
  }
  const s = String(value).slice(0, String(hi).length);
  return s >= String(lo) && s <= String(hi);
}

/** Whether one cell satisfies one condition. */
export function matchCell(value: unknown, matcher: RowMatcher, row: Row): boolean {
  if (Array.isArray(matcher)) return matcher.some((m) => String(value) === String(m));
  if (typeof matcher === 'object' && matcher !== null) {
    if ('between' in matcher) return inRange(value, matcher.between[0], matcher.between[1]);
    if ('below' in matcher) return Number(value) < Number(row[matcher.below]);
    if ('not' in matcher) return String(value) !== String(matcher.not);
  }
  return String(value) === String(matcher);
}

/** Demo line 1907. `true` when every condition in the filter holds. */
export function matchRow(row: object, filter: RowFilter): boolean {
  const r = row as Row;
  return Object.entries(filter).every(([key, matcher]) => matchCell(r[key], matcher, r));
}

/** Apply a filter. An empty filter returns the rows unchanged. */
export function filterRows<T extends object>(rows: readonly T[], filter: RowFilter): readonly T[] {
  return Object.keys(filter).length ? rows.filter((row) => matchRow(row, filter)) : rows;
}

function describeMatcher(matcher: RowMatcher): string {
  if (Array.isArray(matcher)) {
    // The demo prints every value; a forty-machine list would swamp the line.
    return matcher.length > 6
      ? `${matcher.slice(0, 5).join('|')}|… (${matcher.length})`
      : matcher.join('|');
  }
  if (typeof matcher === 'object' && matcher !== null) {
    if ('between' in matcher) return `${matcher.between[0]}…${matcher.between[1]}`;
    if ('below' in matcher) return `<${matcher.below}`;
    if ('not' in matcher) return `≠${matcher.not}`;
  }
  return String(matcher);
}

/**
 * Demo line 1908. The provenance suffix — `'machine_id=T-09, date=2026-08-31…2026-09-06'`.
 * The demo printed a function as `…`; the port can say what the range was.
 */
export function describeFilter(filter: RowFilter): string {
  return Object.entries(filter)
    .map(([key, matcher]) => {
      const text = describeMatcher(matcher);
      return text.startsWith('<') || text.startsWith('≠') ? `${key}${text}` : `${key}=${text}`;
    })
    .join(', ');
}

/** Indexes of the rows a filter selects — how a payload carries highlighted rows. */
export function matchingIndexes(rows: readonly object[], filter: RowFilter | undefined): number[] {
  if (!filter || !Object.keys(filter).length) return [];
  const out: number[] = [];
  rows.forEach((row, i) => {
    if (matchRow(row, filter)) out.push(i);
  });
  return out;
}
